import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Criteria from '@clearscore/rainbow.criteria';
import Eye from '@clearscore/rainbow.icons.show';
import EyeClosed from '@clearscore/rainbow.icons.show-disabled';
import TextInput from '@clearscore/rainbow.text-input';
import toTitleCase from '@clearscore/normalize.title-case';

import styles from './password.css';
import { themes } from './constants';

const proccessRequirements = (requirements, inputValue = '') => {
    if (Array.isArray(requirements) && requirements.length) {
        return requirements.map(({ id, text, validator }) => ({
            id,
            text,
            isValid: validator(inputValue),
        }));
    }

    return false;
};

const Password = ({
    theme,
    dataId,
    requirements: rawRequirements,
    onChange,
    iconTitles,
    errorMessage: inputErrorMessage,
    ...inputProps
}) => {
    const [isEyeOpen, setIsEyeOpen] = useState(false);
    const [requirements, setRequirements] = useState(proccessRequirements(rawRequirements));

    const type = isEyeOpen ? 'text' : 'password';
    const Icon = isEyeOpen ? Eye : EyeClosed;
    const title = isEyeOpen ? iconTitles.eye : iconTitles.eyeClosed;
    const themeClass = styles[`isTheme${toTitleCase(theme)}`];
    const errorMessage = requirements.length ? '' : inputErrorMessage;

    return (
        <div data-id={dataId} data-qa="password">
            <div className={styles.inputWrapper}>
                <TextInput
                    {...inputProps}
                    type={type}
                    theme={theme}
                    autoCapitalize="none"
                    onChange={({ target }) => {
                        onChange(target.value);
                        setRequirements(proccessRequirements(rawRequirements, target.value));
                    }}
                    errorMessage={errorMessage}
                />
                <button
                    className={cx(styles.eye, themeClass)}
                    type="button"
                    onClick={() => setIsEyeOpen(!isEyeOpen)}
                    data-qa="password-eye"
                >
                    <Icon width={26} height={26} title={title} />
                </button>
            </div>
            <If condition={requirements}>
                {requirements.map(({ id, text, isValid }) => {
                    const criteriaType = isValid ? Criteria.types.SUCCESS : Criteria.types.ERROR;
                    return (
                        <Criteria key={id} data-qa={`password-requirement-${id}`} type={criteriaType} theme={theme}>
                            {text}
                        </Criteria>
                    );
                })}
            </If>
        </div>
    );
};

Password.propTypes = {
    theme: PropTypes.oneOf(Object.values(themes)),
    dataId: PropTypes.string,
    requirements: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            text: PropTypes.string,
            validator: PropTypes.func,
        }),
    ),
    onChange: PropTypes.func.isRequired,
    iconTitles: PropTypes.shape({
        eye: PropTypes.string,
        eyeClosed: PropTypes.string,
    }),
    errorMessage: PropTypes.string,
};

Password.defaultProps = {
    theme: themes.LIGHT,
    dataId: undefined,
    requirements: [],
    errorMessage: '',
    iconTitles: {
        eye: 'hide password',
        eyeClosed: 'show password',
    },
};

Password.themes = themes;

export default Password;
